/* eslint-disable */
import React, { useEffect, useMemo, useState } from "react";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import TeacherBehaviourEngine from "../components/admin/teachers/TeacherBehaviourEngine";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  setDoc,
  where,
  writeBatch,
  serverTimestamp,
  increment,
} from "firebase/firestore";
import {
  FadeUpStagger,
  MotionCard,
  MotionButton,
} from "../components/motion/AdminMotion";

function toLocalDateKey(date = new Date()) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function todayKey() {
  return toLocalDateKey(new Date());
}

function formatTodayLabel(date = new Date()) {
  return date.toLocaleDateString([], {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function isWeekendDate(date = new Date()) {
  const day = date.getDay();
  return day === 0 || day === 6;
}

function normaliseClosedDates(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean).map(String);
  if (typeof value === "object") {
    return Object.keys(value).filter((key) => Boolean(value[key]));
  }
  return [];
}

function monthKeyFromDateKey(dateKey) {
  return String(dateKey || "").slice(0, 7);
}

function dayFromDateKey(dateKey) {
  const parts = String(dateKey || "").split("-");
  return Number(parts[2] || 0);
}

function displayStudentName(student) {
  return (
    student?.name ||
    student?.fullName ||
    student?.displayName ||
    "Unnamed Student"
  );
}

function classDisplayName(item) {
  return (
    item?.name ||
    item?.className ||
    item?.yearGroup ||
    item?.label ||
    item?.id ||
    "Class"
  );
}

function classSortScore(label) {
  const text = String(label || "").trim().toLowerCase();

  if (text === "nursery") return 0;
  if (text === "reception") return 1;

  const yearMatch = text.match(/^year\s*(\d{1,2})$/i);
  if (yearMatch) return 100 + Number(yearMatch[1]);

  return 1000;
}

function sortClassesForSchool(classes) {
  return [...classes].sort((a, b) => {
    const scoreA = classSortScore(classDisplayName(a));
    const scoreB = classSortScore(classDisplayName(b));

    if (scoreA !== scoreB) return scoreA - scoreB;
    return String(classDisplayName(a)).localeCompare(
      String(classDisplayName(b))
    );
  });
}

function getStatusTone(status) {
  const s = String(status || "").toLowerCase();

  if (s === "present") {
    return {
      pillBg: "#dcfce7",
      pillColor: "#166534",
      pillBorder: "1px solid #bbf7d0",
      accent: "#22c55e",
      cardBg: "linear-gradient(180deg,#ffffff 0%,#f8fff9 100%)",
    };
  }

  if (s === "late") {
    return {
      pillBg: "#fef3c7",
      pillColor: "#92400e",
      pillBorder: "1px solid #fde68a",
      accent: "#f59e0b",
      cardBg: "linear-gradient(180deg,#ffffff 0%,#fffaf3 100%)",
    };
  }

  if (s === "authorised") {
    return {
      pillBg: "#dbeafe",
      pillColor: "#1d4ed8",
      pillBorder: "1px solid #bfdbfe",
      accent: "#3b82f6",
      cardBg: "linear-gradient(180deg,#ffffff 0%,#f7fbff 100%)",
    };
  }

  return {
    pillBg: "#fee2e2",
    pillColor: "#b91c1c",
    pillBorder: "1px solid #fecaca",
    accent: "#ef4444",
    cardBg: "linear-gradient(180deg,#ffffff 0%,#fff8f8 100%)",
  };
}

function pct(numerator, denominator) {
  if (!denominator) return 0;
  return Math.round((numerator / denominator) * 100);
}



const STATUS_OPTIONS = [
  { key: "present", label: "Present" },
  { key: "late", label: "Late" },
  { key: "absent", label: "Absent" },
  { key: "authorised", label: "Authorised" },
];

const ATTENDANCE_OPEN = {
  hour: 7,
  minute: 0,
  label: "7:00am",
};

const ATTENDANCE_CUTOFF = {
  hour: 16,
  minute: 30,
  label: "4:30pm",
};

const BEHAVIOUR_CUTOFF = {
  hour: 18,
  minute: 0,
  label: "6:00pm",
};

function isAfterCutoff(cutoff, date = new Date()) {
  const limit = new Date(date);

  limit.setHours(cutoff.hour, cutoff.minute, 0, 0);

  return date > limit;
}

function isBeforeCutoff(cutoff, date = new Date()) {
  const limit = new Date(date);

  limit.setHours(cutoff.hour, cutoff.minute, 0, 0);

  return date < limit;
}

function getAttendanceLockMessage({
  schoolClosure,
  beforeAttendanceOpen,
  afterAttendanceCutoff,
}) {
  if (schoolClosure?.isClosed) {
    return schoolClosure.reason
      ? `Attendance register is closed today: ${schoolClosure.reason}.`
      : "Attendance register is closed today.";
  }

  if (beforeAttendanceOpen) {
    return `Attendance register opens at ${ATTENDANCE_OPEN.label}.`;
  }

  if (afterAttendanceCutoff) {
    return `Attendance register is locked after ${ATTENDANCE_CUTOFF.label}.`;
  }

  return "";
}

export default function TeacherDashboard() {
  const { user, profile, loading: authLoading, logout } = useAuth();

  const schoolId = profile?.schoolId || null;
  const teacherUid = user?.uid || "";
  const teacherName =
    profile?.fullName || profile?.displayName || user?.email || "Teacher";

  const assignedClassIdsFromProfile = Array.isArray(profile?.assignedClassIds)
    ? profile.assignedClassIds.filter(Boolean)
    : [];

  const [schoolBranding, setSchoolBranding] = useState({
    schoolName: "Your School",
    logoUrl: "",
    primaryColor: "#4f46e5",
  });
  
  const today = new Date();
  const dateKey = todayKey();
  const todayLabel = formatTodayLabel(today);
  const isWeekend = isWeekendDate(today);
  const beforeAttendanceOpen = isBeforeCutoff(ATTENDANCE_OPEN);
  const afterAttendanceCutoff = isAfterCutoff(ATTENDANCE_CUTOFF);

  const [schoolClosure, setSchoolClosure] = useState({
    checking: true,
    isClosed: false,
    reason: "",
  });

  const attendanceLocked =
    schoolClosure.isClosed || beforeAttendanceOpen || afterAttendanceCutoff;
  const behaviourLocked = isAfterCutoff(BEHAVIOUR_CUTOFF);

  const [classes, setClasses] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState("");
  const [students, setStudents] = useState([]);
  const [attendanceMap, setAttendanceMap] = useState({});
  const [registerRowsReady, setRegisterRowsReady] = useState(false);

  const [loadingClasses, setLoadingClasses] = useState(true);
  const [loadingStudents, setLoadingStudents] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [registerStatus, setRegisterStatus] = useState("open");
  const [showConfirm, setShowConfirm] = useState(false);
  const [showReopenRequest, setShowReopenRequest] = useState(false);
  const [reopenReason, setReopenReason] = useState("");
  const [requestingReopen, setRequestingReopen] = useState(false);
  const [reopenRequestStatus, setReopenRequestStatus] = useState("");
  const [search, setSearch] = useState("");
  const [activePanel, setActivePanel] = useState("classes");

  const registerId = selectedClassId ? `${dateKey}_${selectedClassId}` : "";

  const selectedClass = useMemo(
    () => classes.find((c) => c.id === selectedClassId) || null,
    [classes, selectedClassId]
  );

  const schoolName = schoolBranding.schoolName || "Your School";
  const brandPrimary = schoolBranding.primaryColor || "#4f46e5";

  const registerLabel = schoolClosure.checking
    ? "Checking"
    : schoolClosure.isClosed
    ? "Closed"
    : registerStatus === "reopened"
    ? "Reopened"
    : registerStatus === "submitted"
    ? "Submitted"
    : beforeAttendanceOpen
    ? "Not open"
    : afterAttendanceCutoff
    ? "Locked"
    : "Open";

  const registerStatusColor =
    registerLabel === "Submitted"
      ? "#027a48"
      : registerLabel === "Reopened"
      ? "#2563eb"
      : registerLabel === "Open"
      ? brandPrimary
      : "#b45309";

  const registerIsSubmitted = registerStatus === "submitted";
  const registerIsReopened = registerStatus === "reopened";
  const attendanceLockMessage = getAttendanceLockMessage({
    schoolClosure,
    beforeAttendanceOpen,
    afterAttendanceCutoff,
  });

  const canEditAttendance =
    !schoolClosure.isClosed &&
    !schoolClosure.checking &&
    !registerIsSubmitted &&
    (registerIsReopened || (!beforeAttendanceOpen && !afterAttendanceCutoff));

  const canRequestReopen =
    !schoolClosure.isClosed &&
    !schoolClosure.checking &&
    registerIsSubmitted &&
    reopenRequestStatus !== "pending";

  const selectedStudentIds = useMemo(() => new Set(students.map((student) => student.id)), [students]);

  const currentAttendanceRows = useMemo(() => {
    return Object.values(attendanceMap).filter((item) =>
      selectedStudentIds.has(item?.studentId || item?.id)
    );
  }, [attendanceMap, selectedStudentIds]);

  const currentAttendanceMap = useMemo(() => {
    const next = {};
    currentAttendanceRows.forEach((item) => {
      const key = item?.studentId || item?.id;
      if (key) next[key] = item;
    });
    return next;
  }, [currentAttendanceRows]);

  const filteredStudents = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return students;

    return students.filter((student) => {
      const hay = [
        displayStudentName(student),
        student.admissionNumber,
        student.admissionNo,
        student.classId,
        student.className,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return hay.includes(q);
    });
  }, [students, search]);

  const summary = useMemo(() => {
    let present = 0;
    let late = 0;
    let absent = 0;
    let authorised = 0;

    currentAttendanceRows.forEach((item) => {
      const status = String(item?.status || "present").toLowerCase();
      if (status === "present") present += 1;
      else if (status === "late") late += 1;
      else if (status === "absent") absent += 1;
      else if (status === "authorised") authorised += 1;
    });

    const total = students.length;
    const marked = Math.min(currentAttendanceRows.length, total);
    const completion = pct(marked, total);
    const attendanceRate = pct(present + late + authorised, marked || total);
    return {
      total,
      present,
      late,
      absent,
      authorised,
      marked,
      completion,
      attendanceRate,
      unmarked: Math.max(0, total - marked),
    };
  }, [currentAttendanceRows, students]);

  useEffect(() => {
    async function loadBranding() {
      if (!schoolId) return;

      try {
        const snap = await getDoc(
          doc(db, "schools", schoolId, "settings", "school")
        );

        if (snap.exists()) {
          const data = snap.data() || {};
          setSchoolBranding({
            schoolName: data.schoolName || data.name || "Your School",
            logoUrl: data.logoUrl || "",
            primaryColor: data.primaryColor || "#4f46e5",
          });
        } else {
          setSchoolBranding({
            schoolName: "Your School",
            logoUrl: "",
            primaryColor: "#4f46e5",
          });
        }
      } catch (err) {
        console.error(err);
      }
    }

    loadBranding();
  }, [schoolId]);


  useEffect(() => {
    let cancelled = false;

    async function loadSchoolClosure() {
      if (!schoolId) {
        setSchoolClosure({ checking: false, isClosed: false, reason: "" });
        return;
      }

      if (isWeekend) {
        setSchoolClosure({
          checking: false,
          isClosed: true,
          reason: "Weekend",
        });
        return;
      }

      setSchoolClosure({ checking: true, isClosed: false, reason: "" });

      try {
        const [bankHolidayResult, attendanceSettingsSnap, calendarSettingsSnap] =
          await Promise.allSettled([
            fetch("https://www.gov.uk/bank-holidays.json", { cache: "force-cache" }).then(
              (res) => (res.ok ? res.json() : null)
            ),
            getDoc(doc(db, "schools", schoolId, "settings", "attendance")),
            getDoc(doc(db, "schools", schoolId, "settings", "calendar")),
          ]);

        if (cancelled) return;

        const bankHolidayData =
          bankHolidayResult.status === "fulfilled" ? bankHolidayResult.value : null;
        const englandWalesEvents =
          bankHolidayData?.["england-and-wales"]?.events || [];
        const bankHoliday = englandWalesEvents.find(
          (event) => event?.date === dateKey
        );

        const attendanceSettings =
          attendanceSettingsSnap.status === "fulfilled" && attendanceSettingsSnap.value.exists()
            ? attendanceSettingsSnap.value.data() || {}
            : {};
        const calendarSettings =
          calendarSettingsSnap.status === "fulfilled" && calendarSettingsSnap.value.exists()
            ? calendarSettingsSnap.value.data() || {}
            : {};

        const localClosedDates = [
          ...normaliseClosedDates(attendanceSettings.closedDates),
          ...normaliseClosedDates(attendanceSettings.closureDates),
          ...normaliseClosedDates(attendanceSettings.schoolClosureDates),
          ...normaliseClosedDates(calendarSettings.closedDates),
          ...normaliseClosedDates(calendarSettings.closureDates),
          ...normaliseClosedDates(calendarSettings.schoolClosureDates),
        ];

        if (bankHoliday) {
          setSchoolClosure({
            checking: false,
            isClosed: true,
            reason: bankHoliday.title || "Bank holiday",
          });
          return;
        }

        if (localClosedDates.includes(dateKey)) {
          setSchoolClosure({
            checking: false,
            isClosed: true,
            reason: "School closure day",
          });
          return;
        }

        setSchoolClosure({ checking: false, isClosed: false, reason: "" });
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setSchoolClosure({ checking: false, isClosed: false, reason: "" });
        }
      }
    }

    loadSchoolClosure();

    return () => {
      cancelled = true;
    };
  }, [schoolId, dateKey, isWeekend]);

  useEffect(() => {
    async function loadClasses() {
      if (!schoolId || !teacherUid) {
        setClasses([]);
        setLoadingClasses(false);
        return;
      }

      setLoadingClasses(true);
      setError("");

      try {
        const snap = await getDocs(collection(db, "schools", schoolId, "classes"));

        const rows = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((item) => !item.deletedAt)
          .filter((item) => {
            const assignedTeacherIds = Array.isArray(item.teacherIds)
              ? item.teacherIds
              : [];
            const assignedTeacherUids = Array.isArray(item.teacherUids)
              ? item.teacherUids
              : [];

            return (
              item.teacherId === teacherUid ||
              item.teacherUid === teacherUid ||
              assignedTeacherIds.includes(teacherUid) ||
              assignedTeacherUids.includes(teacherUid) ||
              assignedClassIdsFromProfile.includes(item.id)
            );
          });

        const sortedRows = sortClassesForSchool(rows);

        setClasses(sortedRows);
        setSelectedClassId((prev) => {
          if (prev && sortedRows.some((x) => x.id === prev)) return prev;
          return sortedRows[0]?.id || "";
        });
      } catch (err) {
        console.error(err);
        setError("Failed to load classes.");
      } finally {
        setLoadingClasses(false);
      }
    }

    loadClasses();
  }, [schoolId, teacherUid, assignedClassIdsFromProfile.join("|")]);

  useEffect(() => {
    async function loadStudents() {
      if (!schoolId || !selectedClassId) {
        setStudents([]);
        setLoadingStudents(false);
        return;
      }

      setLoadingStudents(true);
      setError("");

      try {
        const snap = await getDocs(
          query(
            collection(db, "schools", schoolId, "students"),
            where("classId", "==", selectedClassId)
          )
        );

        const rows = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((item) => !item.deletedAt)
          .sort((a, b) =>
            String(displayStudentName(a)).localeCompare(
              String(displayStudentName(b))
            )
          );

        setStudents(rows);
      } catch (err) {
        console.error(err);
        setError("Failed to load students.");
      } finally {
        setLoadingStudents(false);
      }
    }

    loadStudents();
  }, [schoolId, selectedClassId]);

  useEffect(() => {
    setAttendanceMap({});
    setRegisterRowsReady(false);
    setReopenRequestStatus("");
    setRegisterStatus("open");
  }, [registerId]);

  useEffect(() => {
    if (!schoolId || !registerId || !students.length || schoolClosure.checking) {
      setAttendanceMap({});
      setRegisterRowsReady(false);
      return;
    }

    if (schoolClosure.isClosed) {
      setAttendanceMap({});
      setRegisterStatus("closed");
      setRegisterRowsReady(true);
      return;
    }

    const registerRef = doc(db, "schools", schoolId, "liveRegisters", registerId);
    const rowsRef = collection(
      db,
      "schools",
      schoolId,
      "liveRegisters",
      registerId,
      "rows"
    );

    const unsubRegister = onSnapshot(
      registerRef,
      async (snap) => {
        if (!snap.exists()) {
          if (beforeAttendanceOpen) {
            setRegisterStatus("not_open");
            setAttendanceMap({});
            setRegisterRowsReady(true);
            return;
          }

          try {
            const batch = writeBatch(db);

            batch.set(registerRef, {
              schoolId,
              classId: selectedClassId,
              className: classDisplayName(selectedClass),
              dateKey,
              teacherUid,
              teacherName,
              status: "open",
              startedAt: serverTimestamp(),
              submittedAt: null,
              monthKey: monthKeyFromDateKey(dateKey),
              day: dayFromDateKey(dateKey),
              totals: {
                total: students.length,
                present: students.length,
                late: 0,
                absent: 0,
                authorised: 0,
                marked: students.length,
              },
              updatedAt: serverTimestamp(),
            });

            students.forEach((student) => {
              const rowRef = doc(
                db,
                "schools",
                schoolId,
                "liveRegisters",
                registerId,
                "rows",
                student.id
              );

              batch.set(rowRef, {
                studentId: student.id,
                name: displayStudentName(student),
                classId: selectedClassId,
                dateKey,
                status: "present",
                note: "",
                updatedAt: serverTimestamp(),
                updatedBy: teacherUid,
                updatedByName: teacherName,
              });
            });

            await batch.commit();
          } catch (err) {
            console.error(err);
            setError("Live register could not be opened.");
          }
          return;
        }

        const data = snap.data() || {};
        setRegisterStatus(String(data.status || "open"));
        setReopenRequestStatus(String(data.reopenRequest?.status || ""));
      },
      (err) => {
        console.error(err);
        setError("Live register could not be opened.");
      }
    );

    const unsubRows = onSnapshot(
      rowsRef,
      (snap) => {
        const next = {};
        snap.forEach((d) => {
          next[d.id] = { id: d.id, ...d.data() };
        });

        if (!Object.keys(next).length) {
          const initial = {};
          students.forEach((student) => {
            initial[student.id] = {
              studentId: student.id,
              name: displayStudentName(student),
              status: "present",
              note: "",
            };
          });
          setAttendanceMap(initial);
        } else {
          setAttendanceMap(next);
        }

        setRegisterRowsReady(true);
      },
      (err) => {
        console.error(err);
        setError("Could not load register rows.");
      }
    );

    return () => {
      unsubRegister();
      unsubRows();
    };
  }, [
    schoolId,
    registerId,
    students,
    selectedClassId,
    selectedClass,
    teacherUid,
    teacherName,
    dateKey,
    schoolClosure.checking,
    schoolClosure.isClosed,
    beforeAttendanceOpen,
  ]);

  async function writeRegisterTotals(nextMap) {
    if (!schoolId || !registerId) return;

    const values = Object.values(nextMap).filter((item) =>
      selectedStudentIds.has(item?.studentId || item?.id)
    );
    const present = values.filter((x) => x.status === "present").length;
    const late = values.filter((x) => x.status === "late").length;
    const absent = values.filter((x) => x.status === "absent").length;
    const authorised = values.filter((x) => x.status === "authorised").length;
    const marked = Math.min(present + late + absent + authorised, students.length);

    await setDoc(
      doc(db, "schools", schoolId, "liveRegisters", registerId),
      {
        totals: {
          total: students.length,
          present,
          late,
          absent,
          authorised,
          marked,
        },
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
  }

async function handleStatusChange(student, status) {
  if (!schoolId || !registerId || !canEditAttendance) return;

    const next = {
      ...attendanceMap,
      [student.id]: {
        ...(attendanceMap[student.id] || {}),
        studentId: student.id,
        name: displayStudentName(student),
        status,
        note: attendanceMap[student.id]?.note || "",
      },
    };

    setAttendanceMap(next);

    try {
      await setDoc(
        doc(
          db,
          "schools",
          schoolId,
          "liveRegisters",
          registerId,
          "rows",
          student.id
        ),
        {
          studentId: student.id,
          name: displayStudentName(student),
          classId: selectedClassId,
          dateKey,
          status,
          note: attendanceMap[student.id]?.note || "",
          updatedAt: serverTimestamp(),
          updatedBy: teacherUid,
          updatedByName: teacherName,
        },
        { merge: true }
      );

      await writeRegisterTotals(next);
    } catch (err) {
      console.error(err);
      setError("Could not update attendance status.");
    }
  }

async function handleNoteChange(student, value) {
  if (!schoolId || !registerId || !canEditAttendance) return;

    const next = {
      ...attendanceMap,
      [student.id]: {
        ...(attendanceMap[student.id] || {}),
        studentId: student.id,
        name: displayStudentName(student),
        status: attendanceMap[student.id]?.status || "present",
        note: value,
      },
    };

    setAttendanceMap(next);

    try {
      await setDoc(
        doc(
          db,
          "schools",
          schoolId,
          "liveRegisters",
          registerId,
          "rows",
          student.id
        ),
        {
          studentId: student.id,
          name: displayStudentName(student),
          classId: selectedClassId,
          dateKey,
          status: next[student.id].status,
          note: value,
          updatedAt: serverTimestamp(),
          updatedBy: teacherUid,
          updatedByName: teacherName,
        },
        { merge: true }
      );
    } catch (err) {
      console.error(err);
      setError("Could not save note.");
    }
  }

  async function handleSaveDraft() {
    if (!schoolId || !registerId) return;

    if (schoolClosure.isClosed) {
      setError("Attendance is not required today because the school is closed.");
      return;
    }

    if ((beforeAttendanceOpen || afterAttendanceCutoff) && !registerIsReopened) {
      setError(attendanceLockMessage || "Attendance register is not available right now.");
      return;
    }

    if (registerIsSubmitted) {
      setError("This register has already been submitted. Request admin reopening before making changes.");
      return;
    }

    try {
      setSaving(true);
      setError("");
      setMessage("");

      await setDoc(
        doc(db, "schools", schoolId, "liveRegisters", registerId),
        {
          status: registerIsReopened ? "reopened" : "open",
          updatedAt: serverTimestamp(),
          lastSavedAt: serverTimestamp(),
        },
        { merge: true }
      );

      await writeRegisterTotals(currentAttendanceMap);
      setMessage("Draft saved successfully.");
    } catch (err) {
      console.error(err);
      setError("Could not save draft.");
    } finally {
      setSaving(false);
    }
  }

  async function handleSubmitRegister() {
  if (!schoolId || !registerId) return;

  if (schoolClosure.isClosed) {
    setError(
      schoolClosure.reason
        ? `Attendance registers cannot be submitted today because the school is closed: ${schoolClosure.reason}.`
        : "Attendance registers cannot be submitted today because the school is closed."
    );
    return;
  }

  if ((beforeAttendanceOpen || afterAttendanceCutoff) && !registerIsReopened) {
    setError(attendanceLockMessage || "Attendance register is not available right now.");
    return;
  }

  if (registerIsSubmitted) {
    setError(
      "This register has already been submitted. Request admin reopening before making changes."
    );
    return;
  }

  try {
    setSubmitting(true);
    setError("");
    setMessage("");

    const className = selectedClass ? classDisplayName(selectedClass) : "";
    const finalRows = students.map((student) => {
      const row =
        currentAttendanceMap[student.id] ||
        attendanceMap[student.id] || {
          status: "present",
          note: "",
        };

      return {
        student,
        status: String(row.status || "present").toLowerCase(),
        note: row.note || "",
      };
    });

    const totals = finalRows.reduce(
      (acc, item) => {
        acc.marked += 1;
        if (item.status === "present") acc.present += 1;
        else if (item.status === "late") acc.late += 1;
        else if (item.status === "authorised") acc.authorised += 1;
        else acc.absent += 1;
        return acc;
      },
      {
        total: students.length,
        marked: 0,
        present: 0,
        late: 0,
        absent: 0,
        authorised: 0,
      }
    );

    const batch = writeBatch(db);
    const registerRef = doc(db, "schools", schoolId, "liveRegisters", registerId);

    finalRows.forEach(({ student, status, note }) => {
      const studentName = displayStudentName(student);
      const dailyRef = doc(
        db,
        "schools",
        schoolId,
        "students",
        student.id,
        "attendance_daily",
        dateKey
      );

      batch.set(
        dailyRef,
        {
          schoolId,
          studentId: student.id,
          studentName,
          name: studentName,
          classId: selectedClassId,
          className,
          dateKey,
          date: dateKey,
          monthKey: monthKeyFromDateKey(dateKey),
          day: dayFromDateKey(dateKey),
          status,
          note,
          sourceRegisterId: registerId,
          registerStatus: "submitted",
          parentVisible: true,
          corrected: registerIsReopened,
          correctedAt: registerIsReopened ? serverTimestamp() : null,
          correctionCount: registerIsReopened ? increment(1) : 0,
          markedBy: teacherUid,
          markedByName: teacherName,
          submittedAt: serverTimestamp(),
          resubmittedAt: registerIsReopened ? serverTimestamp() : null,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
    });

    batch.set(
      registerRef,
      {
        status: "submitted",
        submittedAt: serverTimestamp(),
        resubmittedAt: registerIsReopened ? serverTimestamp() : null,
        updatedAt: serverTimestamp(),
        reopenRequest: null,
        reopenedResolvedAt: registerIsReopened ? serverTimestamp() : null,
        parentDailyWritten: true,
        parentDailyWrittenAt: serverTimestamp(),
        totals,
      },
      { merge: true }
    );

    batch.set(
      doc(db, "schools", schoolId, "auditLogs", `${registerId}_${Date.now()}`),
      {
        action: registerIsReopened
          ? "attendance_register_resubmitted"
          : "attendance_register_submitted",
        schoolId,
        classId: selectedClassId,
        className,
        registerId,
        dateKey,
        actorUid: teacherUid,
        actorName: teacherName,
        role: "teacher",
        totals,
        createdAt: serverTimestamp(),
        meta: {
          parentDailyWritten: true,
          corrected: registerIsReopened,
          studentCount: students.length,
        },
      },
      { merge: true }
    );

    await batch.commit();

    setRegisterStatus("submitted");
    setReopenRequestStatus("");
    setShowConfirm(false);

    setMessage(
      registerIsReopened
        ? "Register resubmitted successfully. Parent attendance has been updated."
        : "Register submitted successfully. Parent attendance has been published."
    );
  } catch (err) {
    console.error(err);
    setError("Could not submit register.");
  } finally {
    setSubmitting(false);
  }
}

  
async function handleRequestReopen() {
  if (!schoolId || !registerId || !canRequestReopen) return;

  const reason = reopenReason.trim();

  if (reason.length < 8) {
    setError("Please enter a clear reason for reopening this register.");
    return;
  }

  try {
    setRequestingReopen(true);
    setError("");
    setMessage("");

    const registerRef = doc(db, "schools", schoolId, "liveRegisters", registerId);

    await setDoc(
      registerRef,
      {
        reopenRequest: {
          status: "pending",
          requestedBy: teacherUid,
          requestedByName: teacherName,
          requestedAt: serverTimestamp(),
          reason,
        },
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );

    const className = selectedClass ? classDisplayName(selectedClass) : "";

    await addDoc(collection(db, "schools", schoolId, "auditLogs"), {
      action: "attendance_reopen_requested",
      schoolId,
      classId: selectedClassId,
      className,
      registerId,
      dateKey,
      actorUid: teacherUid,
      actorName: teacherName,
      role: "teacher",
      reason,
      createdAt: serverTimestamp(),
      meta: {
        status: registerStatus,
        selectedClassId,
      },
    });

    await addDoc(collection(db, "schools", schoolId, "adminNotifications"), {
      type: "attendance_reopen_request",
      title: "Register reopen requested",
      message: `${teacherName} requested to reopen ${className || "a class"} attendance for ${dateKey}.`,
      schoolId,
      classId: selectedClassId,
      className,
      registerId,
      dateKey,
      teacherUid,
      teacherName,
      reason,
      status: "unread",
      priority: "medium",
      actionTab: "attendance-control",
      actionLabel: "Review request",
      actionUrl: `/admin-manager?tab=attendance-control&registerId=${encodeURIComponent(registerId)}&dateKey=${encodeURIComponent(dateKey)}&classId=${encodeURIComponent(selectedClassId)}&status=reopen_requested`,
      createdAt: serverTimestamp(),
      resolvedAt: null,
      readAt: null,
    });

    setReopenRequestStatus("pending");
    setShowReopenRequest(false);
    setReopenReason("");
    setMessage("Reopen request sent to admin.");
  } catch (err) {
    console.error(err);
    setError("Could not send reopen request.");
  } finally {
    setRequestingReopen(false);
  }
}


  const busy = authLoading || loadingClasses || loadingStudents;

  function renderAttendancePanel() {
  return (
    <MotionCard style={styles.panel}>
      <div style={styles.panelHeadRow}>
        <div>
          <div style={styles.panelEyebrow}>Live register</div>
          <h3 style={styles.panelTitle}>
            {selectedClass ? classDisplayName(selectedClass) : "Class register"}
          </h3>
          <div style={styles.panelText}>
            Mark attendance, add notes, and keep the register accurate before
            submission.
          </div>
        </div>

        <div style={styles.searchWrap}>
          <input
            style={styles.searchInput}
            placeholder="Search student"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

{schoolClosure.isClosed ? (
  <div style={styles.closedBox}>
    School is closed today{schoolClosure.reason ? ` — ${schoolClosure.reason}` : ""}. No attendance register is required.
  </div>
) : registerStatus === "reopened" ? (
  <div style={styles.infoBox}>
    Admin has reopened this register. Make the correction and resubmit it when finished.
  </div>
) : registerStatus === "submitted" ? (
  <div style={styles.successBox}>
    This register has been submitted and is now locked.
    {reopenRequestStatus === "pending" ? " Reopen request pending admin review." : ""}
  </div>
) : beforeAttendanceOpen ? (
  <div style={styles.warningBox}>
    {attendanceLockMessage || `Attendance register opens at ${ATTENDANCE_OPEN.label}.`}
  </div>
) : afterAttendanceCutoff ? (
  <div style={styles.warningBox}>
    {attendanceLockMessage || `Attendance register is locked after ${ATTENDANCE_CUTOFF.label}.`}
  </div>
) : null}

      {!selectedClassId ? (
        <div style={styles.emptyCard}>
          No class selected. Open one from <strong>My Classes</strong>.
        </div>
      ) : schoolClosure.isClosed ? (
        <div style={styles.emptyCard}>
          Attendance is not required today because the school calendar is closed.
        </div>
      ) : beforeAttendanceOpen && !registerIsReopened && registerStatus !== "submitted" ? (
        <div style={styles.emptyCard}>
          {attendanceLockMessage || `The register will become available at ${ATTENDANCE_OPEN.label}.`}
        </div>
      ) : busy || schoolClosure.checking ? (
        <div style={styles.emptyCard}>Loading register...</div>
      ) : !filteredStudents.length ? (
        <div style={styles.emptyCard}>No students match this search.</div>
      ) : (
        <div style={styles.cardGrid}>
          <FadeUpStagger delay={0.02} gap={0.018}>
            {filteredStudents.map((student) => {
              const row = attendanceMap[student.id] || {
                status: "present",
                note: "",
              };
              const tone = getStatusTone(row.status);

              return (
                <MotionCard
                  key={student.id}
                  style={{
                    ...styles.studentCard,
                    background: tone.cardBg,
                    borderLeft: `4px solid ${tone.accent}`,
                  }}
                >
                  <div style={styles.studentTop}>
                    <div>
                      <div style={styles.studentName}>
                        {displayStudentName(student)}
                      </div>
                      <div style={styles.studentSub}>
                        {student.admissionNumber || student.admissionNo
                          ? `Admission: ${
                              student.admissionNumber || student.admissionNo
                            }`
                          : "Attendance row"}
                      </div>
                    </div>

                    <span
                      style={{
                        ...styles.statusPill,
                        background: tone.pillBg,
                        color: tone.pillColor,
                        border: tone.pillBorder,
                      }}
                    >
                      {String(row.status || "present").charAt(0).toUpperCase() +
                        String(row.status || "present").slice(1)}
                    </span>
                  </div>

                  <div style={styles.statusRow}>
                    {STATUS_OPTIONS.map((item) => {
                      const active = row.status === item.key;
                      const itemTone = getStatusTone(item.key);

                      return (
                        <MotionButton
                          key={item.key}
                          onClick={() => handleStatusChange(student, item.key)}
                          disabled={!canEditAttendance}
                          style={{
                            ...styles.statusBtn,
                            ...(active
                              ? {
                                  background: itemTone.pillBg,
                                  color: itemTone.pillColor,
                                  border: itemTone.pillBorder,
                                }
                              : {}),
                          }}
                        >
                          {item.label}
                        </MotionButton>
                      );
                    })}
                  </div>

                  <div style={styles.noteBlock}>
                    <label style={styles.noteLabel}>Optional note</label>
                    <textarea
                      value={row.note || ""}
                      onChange={(e) => handleNoteChange(student, e.target.value)}
                      placeholder="Add attendance note"
                      style={styles.noteInput}
                      disabled={!canEditAttendance}
                    />
                  </div>
                </MotionCard>
              );
            })}
          </FadeUpStagger>
        </div>
      )}
    </MotionCard>
  );
}

function renderBehaviourPanel() {
  return (
    <>
<TeacherBehaviourEngine
  schoolId={schoolId}
  brandPrimary={brandPrimary}
  teacher={{
    uid: user?.uid || "",
    name:
      profile?.fullName ||
      profile?.displayName ||
      user?.email ||
      "Teacher",
  }}
  selectedClass={selectedClass}
  students={students}
  locked={behaviourLocked}
  daysToKeep={7}
/>
    </>
  );
}

function renderClassesPanel() {
  return (
    <MotionCard style={styles.panel}>
      <div style={styles.panelEyebrow}>My classes</div>
      <h3 style={styles.panelTitle}>Assigned class view</h3>
      <div style={styles.panelText}>
        Staff can see their linked classes and move straight into the live
        register.
      </div>

      {loadingClasses ? (
        <div style={styles.loadingText}>Loading classes...</div>
      ) : classes.length ? (
        <div style={styles.classSummaryGrid}>
          {classes.map((item) => {
            const active = item.id === selectedClassId;
            return (
              <MotionCard
                key={item.id}
                style={{
                  ...styles.classSummaryCard,
                  ...(active ? styles.classSummaryCardActive : {}),
                }}
              >
                <div style={styles.classSummaryName}>
                  {classDisplayName(item)}
                </div>
                <div style={styles.classSummarySub}>
                  {active ? "Currently selected" : "Use for attendance"}
                </div>
                <MotionButton
                  style={styles.classOpenBtn}
                  onClick={() => {
                    setSelectedClassId(item.id);
                    setActivePanel("attendance");
                    setSearch("");
                    setError("");
                    setMessage("");
                  }}
                >
                  Open register
                </MotionButton>
              </MotionCard>
            );
          })}
        </div>
      ) : (
        <div style={styles.emptyCard}>
          No classes are assigned to this teacher yet.
        </div>
      )}
    </MotionCard>
  );
}


  return (
    <div
  style={{
    ...styles.shell,
    "--teacher-brand": brandPrimary,
  }}
>
      <aside style={styles.sidebar}>
        <div style={styles.brandBlock}>
          {schoolBranding.logoUrl ? (
            <img
              src={schoolBranding.logoUrl}
              alt={schoolName}
              style={styles.brandLogo}
            />
          ) : (
            <div style={{ ...styles.brandIcon, background: brandPrimary }}>
              {String(schoolName || "S").trim().charAt(0).toUpperCase()}
            </div>
          )}

          <div>
            <div style={styles.brandEyebrow}>{schoolName}</div>
            <div style={styles.brandTitle}>Teacher Console</div>
          </div>
        </div>

        <div style={styles.schoolCard}>
          <div style={styles.schoolLabel}>School</div>
          <div style={styles.schoolValue}>{schoolName}</div>
        </div>

<div style={styles.sidebarNav}>
  <SidebarButton
    active={activePanel === "classes"}
    label="My Classes"
    onClick={() => setActivePanel("classes")}
  />

  <SidebarButton
    active={activePanel === "attendance"}
    label="Attendance"
    onClick={() => setActivePanel("attendance")}
  />

  <SidebarButton
    active={activePanel === "behaviour"}
    label="Behaviour"
    onClick={() => setActivePanel("behaviour")}
  />
</div>

        <div style={styles.sidebarSpacer} />

        <div style={styles.sidebarFooter}>
  <MotionButton style={styles.sidebarGhost} onClick={logout}>
    Logout
  </MotionButton>
</div>
      </aside>

      <main style={styles.main}>
        <MotionCard style={styles.hero} hover={false}>
          <div style={styles.heroLeft}>
            <div style={{ ...styles.heroEyebrow, color: brandPrimary }}>
              {schoolName} Teacher Workspace
            </div>
            <h1 style={styles.heroTitle}>Live Register Console</h1>
            <p style={styles.heroText}>
              Mark attendance faster, switch classes from your assigned list, and
              manage follow-up with a premium teacher-first workspace.
            </p>

            <div style={styles.heroMetaRow}>
              <MetaChip label="Teacher" value={teacherName} />
              <MetaChip label="Date" value={todayLabel} />
              <MetaChip
                label="Register"
                value={registerLabel}
                valueColor={registerStatusColor}
              />
            </div>
          </div>

          <div style={styles.heroRight}>
<div
  style={{
    ...styles.heroFocusCard,
    background: brandPrimary,
  }}
>
              <div style={styles.heroFocusLabel}>Selected class</div>
              <div style={styles.heroFocusValue}>
                {selectedClass ? classDisplayName(selectedClass) : "No class selected"}
              </div>
              <div style={styles.heroFocusSub}>
                {students.length} student{students.length === 1 ? "" : "s"} loaded
              </div>
            </div>
          </div>
        </MotionCard>

        {error ? <div style={styles.errorBox}>{error}</div> : null}
        {message ? <div style={styles.successBox}>{message}</div> : null}

        <section style={styles.kpiGrid}>
          <FadeUpStagger>
            <StatCard label="Students" value={summary.total} />
            <StatCard label="Present" value={summary.present} tone="success" />
            <StatCard label="Late" value={summary.late} tone="warning" />
            <StatCard label="Absent" value={summary.absent} tone="danger" />
            <StatCard label="Authorised" value={summary.authorised} tone="blue" />
            <StatCard label="Completion" value={`${summary.completion}%`} highlight />
          </FadeUpStagger>
        </section>

        <div
          style={{
            ...styles.contentGrid,
            ...(activePanel === "behaviour"
              ? styles.contentGridBehaviour
              : {}),
          }}
        >
          <div style={styles.leftRail}>
            {activePanel === "attendance" ? renderAttendancePanel() : null}
            {activePanel === "behaviour" ? renderBehaviourPanel() : null}
            {activePanel === "classes" ? renderClassesPanel() : null}
          </div>

          {activePanel !== "behaviour" ? (
            <div style={styles.rightRail}>
              <MotionCard style={styles.sidePanel}>
                <div style={styles.panelEyebrow}>Today at a glance</div>
                <h3 style={styles.sideTitle}>Quick summary</h3>

                <SummaryLine
                  label="Class"
                  value={selectedClass ? classDisplayName(selectedClass) : "None"}
                />
                <SummaryLine
                  label="Marked"
                  value={`${summary.marked}/${summary.total}`}
                />
                <SummaryLine
                  label="Attendance"
                  value={`${summary.attendanceRate}%`}
                />
                <SummaryLine label="Unmarked" value={summary.unmarked} />
              </MotionCard>

              <MotionCard style={styles.sidePanel}>
                <div style={styles.panelEyebrow}>Register actions</div>
                <h3 style={styles.sideTitle}>Finish today’s register</h3>

                <div style={styles.actionStack}>
                  <MotionButton
                    onClick={handleSaveDraft}
                    style={styles.secondaryBtn}
                    disabled={!canEditAttendance || saving || submitting}
                  >
                    {saving ? "Saving..." : "Save Draft"}
                  </MotionButton>

                  <MotionButton
                    onClick={() => setShowConfirm(true)}
                    style={{
                      ...styles.primaryBtn,
                      background: brandPrimary,
                    }}
                    disabled={
                      !canEditAttendance ||
                      saving ||
                      submitting ||
                      !students.length
                    }
                  >
                    {registerIsSubmitted
                      ? "Submitted"
                      : submitting
                      ? "Submitting..."
                      : registerIsReopened
                      ? "Resubmit Register"
                      : "Submit Register"}
                  </MotionButton>

                  {registerIsSubmitted ? (
                    <MotionButton
                      onClick={() => {
                        setError("");
                        setMessage("");
                        setShowReopenRequest(true);
                      }}
                      style={styles.reopenRequestBtn}
                      disabled={!canRequestReopen || requestingReopen}
                    >
                      {reopenRequestStatus === "pending"
                        ? "Reopen requested"
                        : "Request reopen"}
                    </MotionButton>
                  ) : null}
                </div>
              </MotionCard>
            </div>
          ) : null}
        </div>
      </main>


      {showReopenRequest ? (
        <div style={styles.modalOverlay} onClick={() => setShowReopenRequest(false)}>
          <MotionCard style={styles.modalCard} hover={false}>
            <div onClick={(e) => e.stopPropagation()}>
              <div style={styles.modalEyebrow}>Reopen request</div>
              <h3 style={styles.modalTitle}>Ask admin to reopen this register</h3>
              <p style={styles.modalText}>
                Explain why this submitted register needs a correction. Admin will review your request before unlocking it.
              </p>

              <textarea
                value={reopenReason}
                onChange={(e) => setReopenReason(e.target.value)}
                placeholder="Example: I marked one pupil as present but they should be authorised absence."
                style={styles.modalTextarea}
              />

              <div style={styles.modalActions}>
                <MotionButton
                  onClick={() => {
                    setShowReopenRequest(false);
                    setReopenReason("");
                  }}
                  style={styles.secondaryBtn}
                  disabled={requestingReopen}
                >
                  Cancel
                </MotionButton>

                <MotionButton
                  onClick={handleRequestReopen}
                  style={{
                    ...styles.primaryBtn,
                    background: brandPrimary,
                  }}
                  disabled={requestingReopen}
                >
                  {requestingReopen ? "Sending..." : "Send Request"}
                </MotionButton>
              </div>
            </div>
          </MotionCard>
        </div>
      ) : null}

      {showConfirm ? (
        <div style={styles.modalOverlay} onClick={() => setShowConfirm(false)}>
          <MotionCard style={styles.modalCard} hover={false}>
            <div onClick={(e) => e.stopPropagation()}>
              <div style={styles.modalEyebrow}>Final confirmation</div>
              <h3 style={styles.modalTitle}>Submit today’s register?</h3>
              <p style={styles.modalText}>
                Once submitted, this register will be marked as completed for{" "}
                <strong>
                  {selectedClass ? classDisplayName(selectedClass) : "this class"}
                </strong>
                .
              </p>

              <div style={styles.modalStats}>
                <div style={styles.modalStat}>
                  <div style={styles.modalStatLabel}>Students</div>
                  <div style={styles.modalStatValue}>{summary.total}</div>
                </div>
                <div style={styles.modalStat}>
                  <div style={styles.modalStatLabel}>Attendance Rate</div>
                  <div style={styles.modalStatValue}>{summary.attendanceRate}%</div>
                </div>
              </div>

              <div style={styles.modalActions}>
                <MotionButton
                  onClick={() => setShowConfirm(false)}
                  style={styles.secondaryBtn}
                  disabled={submitting}
                >
                  Cancel
                </MotionButton>

                <MotionButton
                  onClick={handleSubmitRegister}
                  style={{
                    ...styles.primaryBtn,
                    background: brandPrimary,
                  }}
                  disabled={submitting}
                >
                  {submitting ? "Submitting..." : "Confirm Submit"}
                </MotionButton>
              </div>
            </div>
          </MotionCard>
        </div>
      ) : null}
    </div>
  );
}

function SidebarButton({ active, label, onClick }) {
  return (
    <MotionButton
      onClick={onClick}
      style={{
        ...styles.sidebarNavBtn,
        ...(active ? styles.sidebarNavBtnActive : {}),
      }}
    >
      {label}
    </MotionButton>
  );
}

function MetaChip({ label, value, valueColor }) {
  return (
    <div style={styles.metaChip}>
      <div style={styles.metaLabel}>{label}</div>
      <div style={{ ...styles.metaValue, color: valueColor || "#111827" }}>
        {value}
      </div>
    </div>
  );
}

function SummaryLine({ label, value }) {
  return (
    <div style={styles.summaryLine}>
      <span style={styles.summaryLabel}>{label}</span>
      <span style={styles.summaryValue}>{value}</span>
    </div>
  );
}

function StatCard({ label, value, tone = "default", highlight = false }) {
  const toneMap = {
    default: { color: "#111827" },
    success: { color: "#16a34a" },
    warning: { color: "#d97706" },
    danger: { color: "#dc2626" },
    blue: { color: "#2563eb" },
  };

  return (
    <div
      style={{
        ...styles.statCard,
        ...(highlight
          ? {
              background: "var(--teacher-brand)",
              color: "#fff",
              boxShadow: "0 14px 28px rgba(79,70,229,0.16)",
            }
          : {}),
      }}
    >
      <div
        style={{
          ...styles.statLabel,
          ...(highlight ? { color: "rgba(255,255,255,0.80)" } : {}),
        }}
      >
        {label}
      </div>

      <div
        style={{
          ...styles.statValue,
          ...(highlight ? {} : toneMap[tone] || toneMap.default),
        }}
      >
        {value}
      </div>
    </div>
  );
}

const styles = {
  shell: {
    display: "grid",
    gridTemplateColumns: "280px minmax(0, 1fr)",
    gap: 14,
    width: "100%",
    height: "100dvh",
    minHeight: 0,
    overflow: "hidden",
    background: "#f4f7fb",
    padding: 12,
    boxSizing: "border-box",
  },

sidebar: {
  background: "linear-gradient(180deg,#0f1b4d 0%,#0f172a 100%)",
  borderRadius: 28,
  padding: 18,
  color: "#fff",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  boxShadow: "0 20px 50px rgba(15,23,42,0.16)",
  height: "100%",
  minHeight: 0,
  overflowX: "hidden",
  overflowY: "auto",
  WebkitOverflowScrolling: "touch",
  overscrollBehavior: "contain",
  position: "relative",
},

  brandBlock: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginBottom: 16,
  },

  brandLogo: {
    width: 48,
    height: 48,
    borderRadius: 14,
    objectFit: "cover",
    background: "#fff",
    padding: 4,
  },

  brandIcon: {
    width: 44,
    height: 44,
    borderRadius: 14,
    display: "grid",
    placeItems: "center",
    fontWeight: 900,
    fontSize: 16,
    color: "#fff",
  },

  brandEyebrow: {
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    color: "rgba(255,255,255,0.6)",
    fontWeight: 800,
  },

  brandTitle: {
    fontSize: 18,
    fontWeight: 900,
    lineHeight: 1.1,
  },

  schoolCard: {
    borderRadius: 18,
    background: "rgba(255,255,255,0.08)",
    border: "1px solid rgba(255,255,255,0.08)",
    padding: 14,
    marginBottom: 16,
  },

  schoolLabel: {
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    color: "rgba(255,255,255,0.65)",
    fontWeight: 800,
  },

  schoolValue: {
    marginTop: 8,
    fontSize: 16,
    fontWeight: 800,
    color: "#fff",
    wordBreak: "break-word",
    lineHeight: 1.3,
  },

  sidebarNav: {
    display: "grid",
    gap: 10,
  },

  sidebarNavBtn: {
    width: "100%",
    textAlign: "left",
    padding: "14px 16px",
    borderRadius: 16,
    border: "1px solid rgba(255,255,255,0.08)",
    background: "rgba(255,255,255,0.04)",
    color: "#fff",
    fontWeight: 800,
    fontSize: 15,
  },

sidebarNavBtnActive: {
  background: "var(--teacher-brand)",
    border: "1px solid rgba(255,255,255,0.14)",
    boxShadow: "0 12px 24px rgba(91,97,255,0.22)",
  },

sidebarSpacer: {
  flex: 1,
  minHeight: 12,
},

sidebarFooter: {
  display: "grid",
  gap: 10,
  marginTop: 14,
},



  sidebarGhost: {
    width: "100%",
    borderRadius: 16,
    padding: "14px 18px",
    border: "1px solid rgba(255,255,255,0.10)",
    background: "rgba(255,255,255,0.04)",
    color: "#fff",
    fontWeight: 800,
    fontSize: 15,
  },

  main: {
    minWidth: 0,
    minHeight: 0,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    gap: 14,
    overflowX: "hidden",
    overflowY: "auto",
    WebkitOverflowScrolling: "touch",
    overscrollBehaviorY: "contain",
    paddingBottom: 64,
    boxSizing: "border-box",
  },

  hero: {
    borderRadius: 30,
    background: "linear-gradient(180deg,#ffffff 0%,#f8fbff 100%)",
    border: "1px solid #dbe7ff",
    padding: 22,
    display: "grid",
    gridTemplateColumns: "1.2fr 0.8fr",
    gap: 16,
    alignItems: "stretch",
  },

  heroLeft: {
    minWidth: 0,
  },

  heroEyebrow: {
    fontSize: 12,
    textTransform: "uppercase",
    letterSpacing: "0.13em",
    fontWeight: 900,
    marginBottom: 10,
  },

  heroTitle: {
    margin: 0,
    fontSize: 46,
    lineHeight: 0.95,
    letterSpacing: "-0.05em",
    color: "#112042",
    fontWeight: 900,
    maxWidth: 520,
  },

  heroText: {
    marginTop: 14,
    marginBottom: 0,
    color: "#64748b",
    fontSize: 15,
    lineHeight: 1.7,
    maxWidth: 620,
  },

  heroMetaRow: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: 12,
    marginTop: 20,
  },

  metaChip: {
    borderRadius: 18,
    border: "1px solid #dbe7ff",
    background: "#ffffff",
    padding: "14px 16px",
  },

  metaLabel: {
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: "0.12em",
    fontWeight: 900,
    color: "#94a3b8",
    marginBottom: 8,
  },

  metaValue: {
    fontSize: 15,
    fontWeight: 900,
    color: "#111827",
    lineHeight: 1.25,
  },

  heroRight: {
    display: "flex",
    alignItems: "stretch",
  },

  heroFocusCard: {
    width: "100%",
    borderRadius: 26,
    padding: 22,
    color: "#fff",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    boxShadow: "0 16px 32px rgba(79,70,229,0.16)",
    minHeight: 170,
  },

  heroFocusLabel: {
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: "0.12em",
    fontWeight: 900,
    color: "rgba(255,255,255,0.80)",
    marginBottom: 10,
  },

  heroFocusValue: {
    fontSize: 30,
    lineHeight: 1.05,
    fontWeight: 900,
    letterSpacing: "-0.04em",
    marginBottom: 10,
  },

  heroFocusSub: {
    fontSize: 14,
    color: "rgba(255,255,255,0.92)",
  },

  errorBox: {
    borderRadius: 18,
    padding: 16,
    background: "#fff1f2",
    border: "1px solid #fecdd3",
    color: "#be123c",
    fontWeight: 800,
  },

  successBox: {
    borderRadius: 18,
    padding: 16,
    background: "#ecfdf3",
    border: "1px solid #bbf7d0",
    color: "#166534",
    fontWeight: 800,
  },

  infoBox: {
    borderRadius: 18,
    padding: 16,
    background: "#eff6ff",
    border: "1px solid #bfdbfe",
    color: "#1d4ed8",
    fontWeight: 800,
    marginBottom: 14,
  },

kpiGrid: {
  display: "grid",
  gridTemplateColumns: "repeat(6, minmax(0, 1fr))",
  gap: 12,
  alignItems: "stretch",
},

  statCard: {
  borderRadius: 20,
  background: "#ffffff",
  border: "1px solid #dbe7ff",
  padding: 16,
  minHeight: 104,
  boxShadow: "0 8px 24px rgba(15,23,42,0.04)",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
},

statLabel: {
  fontSize: 11,
  textTransform: "uppercase",
  letterSpacing: "0.12em",
  fontWeight: 900,
  color: "#64748b",
  marginBottom: 8,
},

statValue: {
  fontSize: 30,
  fontWeight: 900,
  lineHeight: 1,
},

  contentGrid: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) 320px",
    gap: 16,
    alignItems: "start",
    minWidth: 0,
  },

  contentGridBehaviour: {
    gridTemplateColumns: "minmax(0, 1fr)",
  },

  leftRail: {
    minWidth: 0,
    minHeight: 0,
    display: "grid",
    gap: 16,
  },

  rightRail: {
    minWidth: 0,
    minHeight: 0,
    display: "grid",
    gap: 16,
  },

  panel: {
    borderRadius: 24,
    background: "#ffffff",
    border: "1px solid #dbe7ff",
    padding: 20,
    boxShadow: "0 8px 24px rgba(15,23,42,0.04)",
  },

  sidePanel: {
    borderRadius: 24,
    background: "#ffffff",
    border: "1px solid #dbe7ff",
    padding: 20,
    boxShadow: "0 8px 24px rgba(15,23,42,0.04)",
  },

warningBox: {
  padding: "14px 16px",
  borderRadius: 18,
  background: "#fff7ed",
  border: "1px solid #fed7aa",
  color: "#9a3412",
  fontWeight: 850,
  marginBottom: 14,
},

closedBox: {
  padding: "14px 16px",
  borderRadius: 18,
  background: "#eff6ff",
  border: "1px solid #bfdbfe",
  color: "#1d4ed8",
  fontWeight: 850,
  marginBottom: 14,
},

  panelHeadRow: {
    display: "grid",
    gridTemplateColumns: "1fr 240px",
    gap: 16,
    alignItems: "end",
    marginBottom: 18,
  },

  panelEyebrow: {
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: "0.12em",
    fontWeight: 900,
    color: "#64748b",
    marginBottom: 8,
  },

  panelTitle: {
    margin: 0,
    fontSize: 20,
    lineHeight: 1.05,
    color: "#112042",
    fontWeight: 900,
    letterSpacing: "-0.03em",
  },

  panelText: {
    marginTop: 10,
    color: "#64748b",
    lineHeight: 1.65,
    fontSize: 14,
  },

  searchWrap: {
    display: "flex",
    justifyContent: "flex-end",
  },

  searchInput: {
    width: "100%",
    padding: "13px 15px",
    borderRadius: 16,
    border: "1px solid #dbe7ff",
    background: "#ffffff",
    outline: "none",
    fontSize: 14,
    color: "#111827",
    boxSizing: "border-box",
  },

  loadingText: {
    color: "#64748b",
    fontWeight: 700,
  },

  emptyCard: {
    borderRadius: 18,
    border: "1px solid #dbe7ff",
    background: "#f8fbff",
    padding: 16,
    color: "#64748b",
    fontWeight: 700,
  },

  cardGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 14,
  },

  studentCard: {
    borderRadius: 20,
    padding: 16,
    boxShadow: "0 8px 20px rgba(15,23,42,0.04)",
  },

  studentTop: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    alignItems: "start",
    marginBottom: 14,
  },

  studentName: {
    fontSize: 18,
    fontWeight: 900,
    color: "#112042",
    lineHeight: 1.1,
  },

  studentSub: {
    marginTop: 6,
    color: "#64748b",
    fontSize: 13,
  },

  statusPill: {
    borderRadius: 999,
    padding: "7px 11px",
    fontSize: 11,
    fontWeight: 900,
    whiteSpace: "nowrap",
  },

  statusRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    marginBottom: 14,
  },

  statusBtn: {
    borderRadius: 12,
    padding: "10px 12px",
    border: "1px solid #dbe7ff",
    background: "#ffffff",
    color: "#112042",
    fontWeight: 800,
    fontSize: 12,
  },

  noteBlock: {
    display: "grid",
    gap: 8,
  },

  noteLabel: {
    fontSize: 12,
    fontWeight: 800,
    color: "#334155",
  },

  noteInput: {
    minHeight: 74,
    resize: "vertical",
    borderRadius: 14,
    border: "1px solid #dbe7ff",
    padding: "11px 12px",
    fontSize: 13,
    outline: "none",
    color: "#111827",
    background: "#ffffff",
    boxSizing: "border-box",
    width: "100%",
  },

  behaviourGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 14,
    marginTop: 18,
  },

  behaviourCard: {
    borderRadius: 18,
    background: "#f8fbff",
    border: "1px solid #dbe7ff",
    padding: 16,
  },

  behaviourTitle: {
    fontSize: 16,
    fontWeight: 900,
    color: "#112042",
    marginBottom: 8,
  },

  behaviourText: {
    color: "#64748b",
    lineHeight: 1.65,
    fontSize: 14,
  },

  classSummaryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 14,
    marginTop: 18,
  },

  classSummaryCard: {
    borderRadius: 18,
    border: "1px solid #dbe7ff",
    background: "#fbfdff",
    padding: 16,
  },

  classSummaryCardActive: {
    background: "linear-gradient(180deg,#eef2ff 0%,#f8fbff 100%)",
    border: "1px solid #c7d2fe",
  },

  classSummaryName: {
    fontSize: 17,
    fontWeight: 900,
    color: "#112042",
    marginBottom: 6,
  },

  classSummarySub: {
    color: "#64748b",
    lineHeight: 1.6,
    marginBottom: 14,
    fontSize: 13,
  },

  classOpenBtn: {
    borderRadius: 12,
    padding: "11px 14px",
    border: "1px solid #dbe7ff",
    background: "#ffffff",
    color: "#112042",
    fontWeight: 800,
    fontSize: 13,
  },

  sideTitle: {
    margin: 0,
    fontSize: 20,
    lineHeight: 1.05,
    color: "#112042",
    fontWeight: 900,
    letterSpacing: "-0.03em",
    marginBottom: 16,
  },

  summaryLine: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    padding: "11px 0",
    borderBottom: "1px solid #e7eefc",
  },

  summaryLabel: {
    color: "#64748b",
    fontWeight: 700,
    fontSize: 14,
  },

  summaryValue: {
    color: "#112042",
    fontWeight: 900,
    fontSize: 14,
  },

  actionStack: {
    display: "grid",
    gap: 10,
  },

  primaryBtn: {
    width: "100%",
    border: "none",
    color: "#fff",
    borderRadius: 16,
    padding: "15px 16px",
    fontWeight: 900,
    fontSize: 14,
    boxShadow: "0 12px 24px rgba(79,70,229,0.16)",
  },

  secondaryBtn: {
    width: "100%",
    borderRadius: 16,
    padding: "14px 16px",
    border: "1px solid #dbe7ff",
    background: "#ffffff",
    color: "#112042",
    fontWeight: 800,
    fontSize: 14,
  },

  modalOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(15,23,42,0.35)",
    display: "grid",
    placeItems: "center",
    padding: 20,
    zIndex: 50,
  },

  modalCard: {
    width: "100%",
    maxWidth: 520,
    borderRadius: 24,
    background: "#ffffff",
    border: "1px solid #dbe7ff",
    padding: 22,
  },

  modalEyebrow: {
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: "0.12em",
    fontWeight: 900,
    color: "#64748b",
    marginBottom: 8,
  },

  modalTitle: {
    margin: 0,
    fontSize: 26,
    lineHeight: 1.02,
    color: "#112042",
    fontWeight: 900,
    letterSpacing: "-0.04em",
  },

  modalText: {
    marginTop: 14,
    color: "#64748b",
    lineHeight: 1.75,
    fontSize: 14,
  },

  modalTextarea: {
    width: "100%",
    minHeight: 120,
    marginTop: 18,
    border: "1px solid #dbe4ff",
    borderRadius: 18,
    padding: 14,
    resize: "vertical",
    fontSize: 14,
    lineHeight: 1.55,
    outline: "none",
    boxSizing: "border-box",
  },

  modalStats: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 12,
    marginTop: 18,
    marginBottom: 22,
  },

  modalStat: {
    borderRadius: 16,
    background: "#f8fbff",
    border: "1px solid #dbe7ff",
    padding: 14,
  },

  modalStatLabel: {
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: "0.12em",
    fontWeight: 900,
    color: "#64748b",
    marginBottom: 8,
  },

  modalStatValue: {
    fontSize: 22,
    fontWeight: 900,
    color: "#112042",
  },

  modalActions: {
    display: "flex",
    gap: 12,
    justifyContent: "flex-end",
  },
};